import React, { useState } from 'react';
import { localizeMessage } from '../utils/toast.js';
import './ApplyProgramModal.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ANSWER_MAX_LEN = 1000;

// Same logistics fields shown in the program detail modal (pp-modal-info-item) — repeated
// here so the applicant sees the location/dates/deadline they're committing to right above
// the form, instead of having to scroll back to the modal underneath.
function ProgramInfoSummary({ program }) {
  const rows = [
    program.location && { icon: 'fa-map-marker-alt', label: 'Location', value: program.location },
    (program.start_date || program.end_date) && {
      icon: 'fa-calendar-check',
      label: 'Dates',
      value: [program.start_date, program.end_date].filter(Boolean).join(' - '),
    },
    program.deadline && { icon: 'fa-calendar-alt', label: 'Deadline', value: program.deadline },
    program.format && { icon: 'fa-layer-group', label: 'Format', value: program.format },
    program.target_group && { icon: 'fa-users', label: 'Target Group', value: program.target_group },
  ].filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div className="apm-info-summary">
      {rows.map((row, i) => (
        <div className="apm-info-row" key={i}>
          <i className={`fas ${row.icon}`}></i>
          <span className="apm-info-label">{row.label}:</span>
          <span className="apm-info-value">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function ApplyProgramModal({ program, questions, apiBase, onClose, onSubmitted }) {
  const [answers, setAnswers] = useState(() => (questions || []).map(() => ''));
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAnswerChange = (idx, value) => {
    setAnswers(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
    setErrors(prev => {
      if (!prev[idx]) return prev;
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) { setFile(null); setFileError(''); return; }
    if (f.size > MAX_FILE_SIZE) {
      setFileError('File is too large. Maximum size is 10MB.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFileError('');
    setFile(f);
  };

  const validate = () => {
    const nextErrors = {};
    (questions || []).forEach((q, idx) => {
      if (q.required && !String(answers[idx] || '').trim()) {
        nextErrors[idx] = 'This field is required';
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      const answersJson = JSON.stringify((questions || []).map((q, idx) => ({
        question: q.text,
        answer: answers[idx] || '',
      })));
      formData.append('answersJson', answersJson);
      if (file) formData.append('file', file);

      const res = await fetch(`${apiBase}/programs/${program._id || program.id}/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      onSubmitted(data.application);
    } catch (err) {
      setSubmitError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="clay-modal-backdrop apm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="clay-modal-enter apm-modal">
        <button type="button" className="pp-modal-close clay-press clay-focus-glow" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <h3 className="apm-title"><i className="fas fa-file-signature"></i> Apply to {program?.title || 'this program'}</h3>
        {program && <ProgramInfoSummary program={program} />}
        <form onSubmit={handleSubmit} className="apm-form">
          {(questions || []).map((q, idx) => (
            <div className="apm-field" key={idx}>
              <label className="apm-label">
                {q.text}{q.required && <span className="apm-required"> *</span>}
              </label>
              <textarea
                className={`apm-textarea${errors[idx] ? ' apm-input-error' : ''}`}
                value={answers[idx]}
                maxLength={ANSWER_MAX_LEN}
                onChange={(e) => handleAnswerChange(idx, e.target.value.slice(0, ANSWER_MAX_LEN))}
                rows={3}
              />
              <span className="apm-char-count">{answers[idx].length}/{ANSWER_MAX_LEN}</span>
              {errors[idx] && <p className="apm-error-msg">{errors[idx]}</p>}
            </div>
          ))}
          <div className="apm-field">
            <label className="apm-label">Attach a file (optional, max 10MB)</label>
            <input type="file" className="apm-file-input" onChange={handleFileChange} />
            {file && <p className="apm-file-name"><i className="fas fa-paperclip"></i> {file.name}</p>}
            {fileError && <p className="apm-error-msg">{fileError}</p>}
          </div>
          {submitError && <p className="apm-error-msg apm-submit-error">{localizeMessage(submitError)}</p>}
          <div className="apm-actions">
            <button type="button" className="pp-btn-ghost clay-press clay-focus-glow" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="pp-apply-btn clay-press clay-focus-glow apm-submit-btn" disabled={submitting}>
              {submitting ? (<><span className="pp-spin-sm"></span> Submitting...</>) : (<><i className="fas fa-paper-plane"></i> Submit Application</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ApplyProgramModal;
